import { ArrowCaret, ArrowSwap, PersonGroup } from '@fs/zion-icon/dist/cjs/icons'
import { css } from '@emotion/core'
import { Button, LabelTooltip, ResponsiveTableRow, Row, Separator } from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import ObjectView from '../../common/ObjectView'
import {
  getMergeHistory,
  getPersonReservationHistory,
  getReservationHistoryQuery,
} from '../../../services/personService'
import { getReservationPersonIds } from '../../../services/reservationsService'
import { utcToLocaleString } from '../../../util/DateUtil'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'
import { downloadFile } from '../../../util/FileDownloadUtil'
import { getEnumAbbreviation, getIdElement } from '../../../util/StringUtil'
import { CisLink, OsidLink, PersonLink, RanLink, UserLink } from '../../app/Links'
import { PersonContext } from '../PersonContext'
import { getUser } from '../../../services/userService'
import FilterOrdinances from '../ordinances/FilterOrdinances'

const sortableColumnHeaderBlock = css`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(0, 0, 0, 0);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.1);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.2);
    font-size: 0.9rem;
  }
`
const sortingOnColumnHeaderBlock = css`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(90, 90, 90, 0.1);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.2);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.3);
    font-size: 0.9rem;
  }
`
const sortableColumnHeaderInline = css`
  display: inline-flex;
  align-items: center;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(0, 0, 0, 0);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.1);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.2);
    font-size: 0.9rem;
  }
`
const sortingOnColumnHeaderInline = css`
  display: inline-flex;
  align-items: center;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(90, 90, 90, 0.1);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.2);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.3);
    font-size: 0.9rem;
  }
`

const SortableColumnHeaderComponent = ({
  fieldType,
  label,
  block,
  sortField,
  sortByFieldType,
  getFieldSortingIcon,
  blockCss,
  sortingOnBlockCss,
  inlineCss,
  sortingOnInlineCss,
}) => {
  return (
    <>
      {block ? (
        <button
          css={sortField === fieldType ? sortingOnBlockCss : blockCss}
          type="button"
          onClick={() => sortByFieldType(fieldType)}
        >
          <span style={{ flex: '1' }}>{label}</span>
          <span>{getFieldSortingIcon(fieldType)}</span>
        </button>
      ) : (
        <button
          css={sortField === fieldType ? sortingOnInlineCss : inlineCss}
          type="button"
          onClick={() => sortByFieldType(fieldType)}
        >
          <span style={{ flex: '1' }}>{label}</span>
          <span>{getFieldSortingIcon(fieldType)}</span>
        </button>
      )}
    </>
  )
}

function PersonReservationsHistoryContent() {
  usePageView({ page_detail: 'DMConsole:PersonReservationsHistory' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.reservationsHistory'))
  const { error } = useNotifications()
  const { currentPerson } = useContext(PersonContext)
  const currentPersonId = currentPerson?.id
  const [reservationHistoryData, setReservationHistoryData] = useState(null)
  const [ranIdToPersonIdMap, setRanIdToPersonIdMap] = useState()
  const [userIdToContactNameMap, setUserIdToContactNameMap] = useState()
  const [sortField, setSortField] = useState('date')
  const [sortType, setSortType] = useState('descending')
  const [personReservationHistories, setPersonReservationHistories] = useState()
  const [ordinanceType, setOrdinanceType] = useState('all')
  const sortedReservationsHistory = sortReservationsHistory()
  const filteredReservationsHistory =
    ordinanceType === 'all'
      ? sortedReservationsHistory
      : sortedReservationsHistory?.filter((e) => e.reservation.ordinanceType === ordinanceType)
  const [reservationHistoryStatuses, setReservationHistoryStatuses] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const hasReservations = sortedReservationsHistory?.length > 0
  const [errorMessages, setErrorMessages] = useState([])
  const [activeFetches, setActiveFetches] = useState(new Set())
  const [waitingQueue, setWaitingQueue] = useState([])
  const retryAttemptsRef = useRef({})
  // ids we have already requested a lookup for, so incrementally arriving
  // reservation history only triggers calls for ids we have not seen yet
  const requestedRanIdsRef = useRef(new Set())
  const requestedUserIdsRef = useRef(new Set())
  const fetchReservationsHistoryInternalRef = useRef(null)
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const sortByFieldType = (field) => {
    if (field !== sortField || sortType === undefined) {
      setSortType('ascending')
      setSortField(field)
    } else if (sortType === 'ascending') {
      setSortType('descending')
    } else if (sortType === 'descending') {
      setSortType(undefined)
      setSortField(undefined)
    }
  }

  const hasErrorStatus = useCallback(
    (status) => {
      return status === t('person.reservations.history.error', 'Error')
    },
    [t]
  )

  const getFieldSortingIcon = (field) => {
    if (field !== sortField || sortType === undefined) {
      return <ArrowSwap size="xs" direction="up" />
    }
    if (sortType === 'ascending') {
      return <ArrowCaret size="xs" direction="up" />
    }
    if (sortType === 'descending') {
      return <ArrowCaret size="xs" direction="down" />
    }
    return null
  }

  const renderSortableColumnHeader = (fieldType, label, block) => (
    <SortableColumnHeaderComponent
      fieldType={fieldType}
      label={label}
      block={block}
      sortField={sortField}
      sortByFieldType={sortByFieldType}
      getFieldSortingIcon={getFieldSortingIcon}
      blockCss={sortableColumnHeaderBlock}
      sortingOnBlockCss={sortingOnColumnHeaderBlock}
      inlineCss={sortableColumnHeaderInline}
      sortingOnInlineCss={sortingOnColumnHeaderInline}
    />
  )

  const smallMediumTableRows = () => {
    return {
      labels: [
        <>
          {renderSortableColumnHeader('pid', t('person.reservations.history.pid', 'PID'))}/
          {renderSortableColumnHeader('ordinanceType', t('person.reservations.history.ord', 'Ord'))}
        </>,
        <>
          {renderSortableColumnHeader('date', t('person.reservations.history.date', 'Date'))}
          {`/${t('person.reservations.history.updateType', 'Update Type')}/${t(
            'person.reservations.history.action',
            'Action'
          )}/`}
          {renderSortableColumnHeader('cisId', t('person.reservations.history.submitterId', 'Submitter ID'))}
          {`/${t('person.reservations.history.contributorId', 'Contributor ID')}`}
        </>,
        <>
          {`${t('person.reservations.history.ranId', 'RAN ID')}/${t(
            'person.reservations.history.resType',
            'Res Type'
          )}/${t('person.reservations.history.owsOSID', 'OWS OSID')}/`}
          {renderSortableColumnHeader('ownerId', t('person.reservations.history.ownerId', 'Owner ID'))}
        </>,
      ],
      columnWidths: [3, 5, 4],
      combinationRules: [
        ['column1', 'column2'],
        ['column3', 'column4', 'column5', 'column6', 'column7'],
        ['column8', 'column9', 'column10', 'column11'],
      ],
    }
  }

  const defaultXxlTableRows = () => ({
    labels: [
      renderSortableColumnHeader('pid', t('person.reservations.history.pid', 'PID'), true),
      renderSortableColumnHeader('ordinanceType', t('person.reservations.history.ord', 'Ord'), true),
      renderSortableColumnHeader('date', t('person.reservations.history.date', 'Date'), true),
      `${t('person.reservations.history.updateType', 'Update Type')}/${t(
        'person.reservations.history.action',
        'Action'
      )}`,
      <>
        {renderSortableColumnHeader('cisId', t('person.reservations.history.submitterId', 'Submitter ID'))}
        {`/${t('person.reservations.history.contributorId', 'Contributor ID')}`}
      </>,
      `${t('person.reservations.history.ranId', 'RAN ID')}/${t(
        'person.reservations.history.resType',
        'Res Type'
      )}/${t('person.reservations.history.owsOSID', 'OWS OSID')}`,
      renderSortableColumnHeader('ownerId', t('person.reservations.history.ownerId', 'Owner ID')),
    ],
    columnWidths: [1, 1, 1, 3, 2, 2, 2],
    combinationRules: [
      ['column1'],
      ['column2'],
      ['column3'],
      ['column4', 'column5'],
      ['column6', 'column7'],
      ['column8', 'column9', 'column10'],
      ['column11'],
    ],
  })

  // Automatically process queue when slots become available
  useEffect(() => {
    if (waitingQueue.length === 0 || activeFetches.size >= 10) return

    const slotsAvailable = 10 - activeFetches.size
    const itemsToProcess = waitingQueue.slice(0, slotsAvailable)

    setActiveFetches((prevActive) => {
      const newActive = new Set(prevActive)
      itemsToProcess.forEach((item) => {
        newActive.add(item.personId)
      })
      return newActive
    })

    setWaitingQueue((prevQueue) => prevQueue.slice(slotsAvailable))

    // Start fetches
    itemsToProcess.forEach((item) => {
      if (fetchReservationsHistoryInternalRef.current) {
        fetchReservationsHistoryInternalRef.current(item.personId, item.getResHistoryFunc, 1)
      }
    })
  }, [activeFetches, waitingQueue])

  const removeFromActiveFetches = useCallback((personId) => {
    setActiveFetches((prevActive) => {
      const newActive = new Set(prevActive)
      newActive.delete(personId)
      return newActive
    })
  }, [])

  const handleCompletedFetch = useCallback(
    (personId, events) => {
      setReservationHistoryStatuses((prevState) => {
        return {
          ...prevState,
          [personId]: t('person.reservations.history.completedResults', 'Completed: {numResults} results', {
            numResults: events.length,
          }),
        }
      })

      setPersonReservationHistories((prevState) => {
        return { ...prevState, [personId]: events }
      })

      delete retryAttemptsRef.current[personId]
      removeFromActiveFetches(personId)
    },
    [t, removeFromActiveFetches]
  )

  const handleFailedFetch = useCallback(
    (personId, statusMessage) => {
      setReservationHistoryStatuses((prevState) => {
        return {
          ...prevState,
          [personId]: statusMessage,
        }
      })
      removeFromActiveFetches(personId)
    },
    [removeFromActiveFetches]
  )

  const scheduleRetry = useCallback(
    (personId) => {
      setWaitingQueue((prevQueue) => [
        ...prevQueue,
        { personId, getResHistoryFunc: () => getPersonReservationHistory(personId) },
      ])
      setReservationHistoryStatuses((prevState) => {
        return {
          ...prevState,
          [personId]: t('person.reservations.history.status.WAITING', 'Waiting'),
        }
      })
    },
    [t]
  )

  const handleThrottlingError = useCallback(
    (personId) => {
      const currentAttempt = retryAttemptsRef.current[personId] || 0

      if (currentAttempt < 5) {
        const nextAttempt = currentAttempt + 1
        const backoffDelay = 2 ** currentAttempt * 1000 // 1s, 2s, 4s, 8s, 16s

        retryAttemptsRef.current[personId] = nextAttempt
        setReservationHistoryStatuses((prevState) => {
          return {
            ...prevState,
            [personId]: t('person.reservations.history.status.RETRYING', 'Retrying ({attempt}/5) in {seconds}s...', {
              attempt: nextAttempt,
              seconds: backoffDelay / 1000,
            }),
          }
        })

        setTimeout(() => {
          if (isMounted.current) scheduleRetry(personId)
        }, backoffDelay)
      } else {
        setReservationHistoryStatuses((prevState) => {
          return {
            ...prevState,
            [personId]: t('person.reservations.history.error', 'Error: Max retries exceeded'),
          }
        })
        setErrorMessages((prevState) => {
          return [...prevState, `${personId}: Max retries exceeded for throttling error`]
        })
      }
    },
    [t, scheduleRetry]
  )

  const handleNonThrottlingError = useCallback(
    (personId, errorMessage) => {
      setReservationHistoryStatuses((prevState) => {
        return {
          ...prevState,
          [personId]: t('person.reservations.history.error', 'Error'),
        }
      })
      setErrorMessages((prevState) => {
        return [...prevState, errorMessage]
      })
      removeFromActiveFetches(personId)
    },
    [t, removeFromActiveFetches]
  )

  const fetchReservationsHistoryInternal = useCallback(
    async (personId, getResHistoryFunc, attempt = 1) => {
      if (!isMounted.current) return
      try {
        setReservationHistoryStatuses((prevState) => {
          return { ...prevState, [personId]: t('person.reservations.history.status.RUNNING', 'Running') }
        })

        const response = await getResHistoryFunc()

        if (!isMounted.current) return
        if (response.data.status === 'COMPLETED') {
          handleCompletedFetch(personId, response.data.events)
        } else if (response.data.status === 'RUNNING') {
          setTimeout(() => {
            if (isMounted.current) {
              fetchReservationsHistoryInternal(
                personId,
                () => getReservationHistoryQuery(response.data.queryExecutionId),
                attempt + 1
              )
            }
          }, attempt * 1000)
        } else {
          handleFailedFetch(
            personId,
            t('person.reservations.history.status.{status}', '{status}', {
              status: response.data.status,
            })
          )
        }
      } catch (err) {
        if (!isMounted.current) return
        const isThrottling =
          err.response?.data?.notifications?.some((notification) =>
            notification.msg?.includes('HIVE_S3_THROTTLING: S3 throttling')
          ) || err.response?.status === 429

        if (isThrottling) {
          handleThrottlingError(personId)
        } else {
          handleNonThrottlingError(personId, err.message)
        }
      }
    },
    [t, handleCompletedFetch, handleFailedFetch, handleThrottlingError, handleNonThrottlingError]
  )

  // Assign to ref to avoid circular dependency
  fetchReservationsHistoryInternalRef.current = fetchReservationsHistoryInternal

  const fetchReservationsHistory = useCallback(
    (personId, getResHistoryFunc) => {
      setActiveFetches((prevActive) => {
        if (prevActive.size < 10) {
          // Start immediately if under limit
          const newActive = new Set(prevActive)
          newActive.add(personId)
          fetchReservationsHistoryInternal(personId, getResHistoryFunc, 1)
          return newActive
        }
        // Add to waiting queue
        setWaitingQueue((prevQueue) => [...prevQueue, { personId, getResHistoryFunc }])
        setReservationHistoryStatuses((prevState) => {
          return { ...prevState, [personId]: t('person.reservations.history.status.WAITING', 'Waiting') }
        })
        return prevActive
      })
    },
    [t, fetchReservationsHistoryInternal]
  )

  useEffect(() => {
    if (loaded) {
      const errorFound = Object.entries(reservationHistoryStatuses).some(([, status]) => hasErrorStatus(status))
      setHasError(errorFound)
      const uniqueErrorMessages = [...new Set(errorMessages)]
      const msg = uniqueErrorMessages.length > 0 ? uniqueErrorMessages.join(', ') : ''
      if (errorFound) {
        error(
          `${t(
            'person.reservationsHistory.errorFetchingPersonReservationHistory',
            `Error retrieving person reservations history`
          )}: ${msg}`
        )
      }
    }
  }, [error, errorMessages, hasErrorStatus, loaded, reservationHistoryStatuses, t])

  useEffect(() => {
    const result = []
    personReservationHistories &&
      Object.entries(personReservationHistories).forEach((entry) => {
        const [key, value] = entry
        value.forEach((reservationEntry) => {
          reservationEntry.personId = key
        })
        result.push(...value)
      })
    personReservationHistories && setReservationHistoryData(result)
  }, [personReservationHistories])

  useEffect(() => {
    if (currentPersonId) {
      fetchReservationsHistory(currentPersonId, () => getPersonReservationHistory(currentPersonId))
    }
  }, [currentPersonId, fetchReservationsHistory])

  const buildInitialMergesMap = (mergeHistoryData) => {
    const initialMerges = {}
    mergeHistoryData.reverse().forEach((item) => {
      if (item.operation === 'Merged' && !initialMerges[item.duplicatePersonId]) {
        initialMerges[item.duplicatePersonId] = item.changeId.slice(0, -4)
      }
    })
    return initialMerges
  }

  const getInitialMerges = useCallback(async () => {
    const response = await getMergeHistory(currentPersonId)
    return buildInitialMergesMap(response.data)
  }, [currentPersonId])

  const hasRestoredOperation = useCallback((mergeData) => {
    return mergeData.some((item) => item.operation === 'Restored')
  }, [])

  const processMergeEntry = useCallback(
    async (duplicatePersonId, changeId) => {
      const mergeResponse = await getMergeHistory(duplicatePersonId)
      const mergeIndex = mergeResponse.data.findIndex((item) => item.changeId.slice(0, -4) === changeId)
      const relevantMergeData = mergeIndex === -1 ? mergeResponse.data : mergeResponse.data.slice(0, mergeIndex)

      if (!hasRestoredOperation(relevantMergeData)) {
        return duplicatePersonId
      }
      return null
    },
    [hasRestoredOperation]
  )

  const getFinalMergeIds = useCallback(
    async (initialMerges) => {
      if (!initialMerges) {
        return []
      }

      const entries = Object.entries(initialMerges)
      const results = await Promise.all(
        entries.map(([duplicatePersonId, changeId]) => processMergeEntry(duplicatePersonId, changeId))
      )

      return results.filter((id) => id !== null)
    },
    [processMergeEntry]
  )

  const fetchRelatedPersonReservationHistories = useCallback(async () => {
    const initialMerges = await getInitialMerges()
    const finalMergedIds = await getFinalMergeIds(initialMerges)
    const uniqueMergedIds = [...new Set(finalMergedIds)]

    const reservationHistoryPromises = uniqueMergedIds.map((mergedId) =>
      fetchReservationsHistory(mergedId, () => getPersonReservationHistory(mergedId))
    )

    await Promise.allSettled(reservationHistoryPromises)
    setLoaded(true)
  }, [getInitialMerges, getFinalMergeIds, fetchReservationsHistory])

  useEffect(() => {
    fetchRelatedPersonReservationHistories(currentPersonId)
  }, [currentPersonId, fetchRelatedPersonReservationHistories])

  // Reservation history arrives incrementally - one Athena query per person (the
  // current person plus every merged duplicate) resolves on its own schedule, so
  // reservationHistoryData grows over time. These lookups must therefore run for
  // each new batch of ids rather than only for whatever was present the first
  // time, otherwise late-arriving rows never get their RAN link/tooltip data.
  useEffect(() => {
    if (!reservationHistoryData) return

    const newRanIds = [
      ...new Set(reservationHistoryData.map((reservationEvent) => reservationEvent.reservation.reservationId)),
    ].filter((ranId) => ranId && !requestedRanIdsRef.current.has(ranId))
    if (newRanIds.length === 0) return
    newRanIds.forEach((ranId) => requestedRanIdsRef.current.add(ranId))

    const fetchReservationPersonIds = async () => {
      const fetchReservationPersonIdsResults = await Promise.allSettled(
        newRanIds.map((ranId) => getReservationPersonIds(ranId))
      )
      const ranIdToPersonMapObject = {}
      fetchReservationPersonIdsResults.forEach((result, index) => {
        const ranId = newRanIds[index]
        if (result.status !== 'fulfilled') {
          // allow a later batch of history data to retry this lookup
          requestedRanIdsRef.current.delete(ranId)
          return
        }
        ranIdToPersonMapObject[ranId] = result.value.data[0]
      })
      if (!isMounted.current) return
      setRanIdToPersonIdMap((prevMap) => ({ ...prevMap, ...ranIdToPersonMapObject }))
    }
    fetchReservationPersonIds()
  }, [reservationHistoryData])

  useEffect(() => {
    if (!reservationHistoryData) return

    // collect the (id, lookup type) pairs we have not requested yet
    const newUserLookups = []
    const seenInThisBatch = new Set()
    const addUserLookup = (userId, type) => {
      if (!userId) return
      const requestKey = `${type}:${userId}`
      if (seenInThisBatch.has(requestKey) || requestedUserIdsRef.current.has(requestKey)) return
      seenInThisBatch.add(requestKey)
      newUserLookups.push({ userId, type, requestKey })
    }
    reservationHistoryData.forEach((reservationEvent) => {
      addUserLookup(reservationEvent.cisId, 'cisId')
      addUserLookup(reservationEvent.proxyCisId, 'cisId')
      addUserLookup(reservationEvent.reservation.ownerId, 'contributorId')
    })
    if (newUserLookups.length === 0) return
    newUserLookups.forEach(({ requestKey }) => requestedUserIdsRef.current.add(requestKey))

    const fetchContactNames = async () => {
      const contactNameResults = await Promise.allSettled(
        newUserLookups.map(({ userId, type }) => getUser(userId, type))
      )
      const userIdToContactNameMapObject = {}
      contactNameResults.forEach((result, index) => {
        const { userId, requestKey } = newUserLookups[index]
        if (result.status !== 'fulfilled') {
          // allow a later batch of history data to retry this lookup
          requestedUserIdsRef.current.delete(requestKey)
          return
        }
        const user = result?.value?.data?.identity?.users?.[0]
        const { systemName, displayName, productName, contactName } = user || {}
        if (userId.startsWith('cis.wkca')) {
          // system account - prefer systemName but fall back so a tooltip still shows
          userIdToContactNameMapObject[userId] = systemName || displayName || productName || contactName
        } else if (userId.startsWith('cis.proc')) {
          // process account - prefer productName but fall back so a tooltip still shows
          userIdToContactNameMapObject[userId] = productName || displayName || systemName || contactName
        } else {
          userIdToContactNameMapObject[userId] = contactName || displayName || productName || systemName
        }
      })
      if (!isMounted.current) return
      setUserIdToContactNameMap((prevMap) => ({ ...prevMap, ...userIdToContactNameMapObject }))
    }
    fetchContactNames()
  }, [reservationHistoryData])

  function sortReservationsHistory() {
    if (!reservationHistoryData) return reservationHistoryData

    const getItem = (item, path) => path.reduce((acc, key) => acc?.[key], item)
    const compare = (left, right, path, transform = (x) => x) => {
      const itemA = transform(getItem(left, path))
      const itemB = transform(getItem(right, path))
      return itemA?.localeCompare?.(itemB) || itemB - itemA
    }

    const sortByField = (path, transform) => {
      const sortedData = [...reservationHistoryData]
      return sortType === 'ascending'
        ? sortedData.sort((a, b) => compare(a, b, path, transform))
        : sortedData.sort((a, b) => compare(b, a, path, transform))
    }

    switch (sortField) {
      case 'date':
        // reservationEvent.timestamp is a raw epoch number, so this is a plain numeric sort key
        // (matches moment.utc(n).valueOf(), which truncates a bare number rather than treating
        // it as a calendar date)
        return [...reservationHistoryData].sort((a, b) => {
          const timeA = Math.trunc(a.timestamp)
          const timeB = Math.trunc(b.timestamp)
          return sortType === 'ascending' ? timeA - timeB : timeB - timeA
        })
      case 'cisId':
        return sortByField(['cisId'], getIdElement)
      case 'pid':
        return sortByField(['personId'], getIdElement)
      case 'ownerId':
        return sortByField(['reservation', 'ownerId'])
      case 'ordinanceType':
        return sortByField(['reservation', 'ordinanceType'])
      default:
        return reservationHistoryData
    }
  }

  const getDataAsStringList = (rows) => {
    const stringList = []
    const columnsRow = `Ordinance Type, Event Timestamp, Update Type, Update Action, Submitter ID, Contributor ID, Request ID, Developer Key, RAN ID, Reservation Type, Owner ID, Owner CIS ID, OWS OSID, Schema Version, Authorization ID, Current Spouse 1 ID, Current Spouse 2 ID, Original Spouse 1 ID, Original Spouse 2 ID, Current Individual ID, Original Individual ID, Create Timestamp, Assign Timestamp, Printable Inventory Timestamp, Select Timestamp, Update Timestamp, Shared with Church, Selected, On List`
    stringList.push([columnsRow])

    if (rows) {
      rows.forEach((reservationEvent) => {
        const data = `${reservationEvent.reservation.ordinanceType},   ${utcToLocaleString(
          reservationEvent.timestamp,
          true
        )},    ${reservationEvent.updateType},    ${reservationEvent.updateAction},    ${reservationEvent.cisId},    ${
          reservationEvent.proxyCisId
        },    ${reservationEvent.requestId},    ${reservationEvent.developerKey},    ${
          reservationEvent.reservation.reservationId
        },    ${reservationEvent.reservation.reservationType},    ${reservationEvent.reservation.ownerId},    ${
          reservationEvent.reservation.ownerCisId
        },    ${reservationEvent.reservation.templeSystemId && reservationEvent.reservation.templeSystemId},    ${
          reservationEvent.reservation.schemaVersion
        },    ${reservationEvent.reservation.authorizationId},    ${reservationEvent.reservation.currentSpouse1Id},    ${
          reservationEvent.reservation.currentSpouse2Id
        },    ${reservationEvent.reservation.originalSpouse1Id},    ${
          reservationEvent.reservation.originalSpouse2Id
        },    ${reservationEvent.reservation.currentIndividualId},    ${
          reservationEvent.reservation.originalIndividualId
        },    ${utcToLocaleString(reservationEvent.reservation.createTimestamp, true)},    ${utcToLocaleString(
          reservationEvent.reservation.assignTimestamp,
          true
        )},    ${utcToLocaleString(
          reservationEvent.reservation.printableInventoryTimestamp,
          true
        )},    ${utcToLocaleString(reservationEvent.reservation.selectTimestamp, true)},    ${utcToLocaleString(
          reservationEvent.reservation.updateTimestamp,
          true
        )},    ${reservationEvent.reservation.sharedWithChurch},    ${reservationEvent.reservation.selected},    ${
          reservationEvent.reservation.onList
        }`
        stringList.push(data)
      })
    }

    return stringList
  }

  const downloadButtonOnClick = () => {
    const reservationHistoryDataAsStringList = getDataAsStringList(filteredReservationsHistory)
    const reservationHistoryDataAsOneString = reservationHistoryDataAsStringList.join('\n')
    downloadFile(reservationHistoryDataAsOneString, `${currentPersonId}-reservations-history.csv`)
  }

  return (
    <>
      {reservationHistoryStatuses && <ObjectView object={reservationHistoryStatuses} noCamelCase />}
      <Separator />
      {(hasReservations || hasError) && (
        <>
          <Row>
            {hasReservations && (
              <>
                <FilterOrdinances
                  currentOrdinanceType={ordinanceType}
                  setOrdinanceType={setOrdinanceType}
                  isReservationList
                />
                <Button emphasis="medium" onClick={downloadButtonOnClick}>
                  {t('action.download', 'Download')}
                </Button>
              </>
            )}
          </Row>

          <Separator size="nano" />
          {filteredReservationsHistory?.length === 0 &&
            t('person.reservations.history.noEventsFound', 'No events found')}
          {filteredReservationsHistory?.length > 0 && (
            <DmcResponsiveTable
              id="Reservations History Table"
              gutters="nano"
              totalColumnCount={11}
              separator={<br />}
              atSizeColumns={{
                default: defaultXxlTableRows(),
                xxs: smallMediumTableRows(),
                xs: smallMediumTableRows(),
                sm: smallMediumTableRows(),
                md: smallMediumTableRows(),
                xl: {
                  labels: [
                    <>
                      {renderSortableColumnHeader('pid', t('person.reservations.history.pid', 'PID'))}/
                      {renderSortableColumnHeader('ordinanceType', t('person.reservations.history.ord', 'Ord'))}
                    </>,
                    <>
                      {renderSortableColumnHeader('date', t('person.reservations.history.date', 'Date'))}
                      {`/${t('person.reservations.history.updateType', 'Update Type')}/${t(
                        'person.reservations.history.action',
                        'Action'
                      )}`}
                    </>,
                    <>
                      {renderSortableColumnHeader(
                        'cisId',
                        t('person.reservations.history.submitterId', 'Submitter ID')
                      )}
                      {`/${t('person.reservations.history.contributorId', 'Contributor ID')}`}
                    </>,
                    `${t('person.reservations.history.ranId', 'RAN ID')}/${t(
                      'person.reservations.history.resType',
                      'Res Type'
                    )}/${t('person.reservations.history.owsOSID', 'OWS OSID')}`,
                    renderSortableColumnHeader('ownerId', t('person.reservations.history.ownerId', 'Owner ID')),
                  ],
                  columnWidths: [1, 4, 3, 3, 1],
                  combinationRules: [
                    ['column1', 'column2'],
                    ['column3', 'column4', 'column5'],
                    ['column6', 'column7'],
                    ['column8', 'column9', 'column10'],
                    ['column11'],
                  ],
                },
                xxl: defaultXxlTableRows(),
              }}
            >
              {filteredReservationsHistory.map((reservationEvent, index, array) => {
                return (
                  <ResponsiveTableRow
                    key={JSON.stringify(reservationEvent)}
                    noBottomDivider={array.length - 1 === index}
                    column1={
                      <span style={{ wordWrap: 'break-word' }}>
                        <PersonLink personId={reservationEvent.personId} />
                      </span>
                    }
                    column2={getEnumAbbreviation(reservationEvent.reservation.ordinanceType)}
                    column3={<span>{utcToLocaleString(reservationEvent.timestamp, true)}</span>}
                    column4={
                      reservationEvent.updateType && (
                        <span style={{ wordWrap: 'break-word' }}>
                          {t(`reservation.updateType.${reservationEvent.updateType}`, reservationEvent.updateType)}
                        </span>
                      )
                    }
                    column5={
                      reservationEvent.updateAction && (
                        <span style={{ wordWrap: 'break-word' }}>
                          {t(
                            `reservation.updateAction.${reservationEvent.updateAction}`,
                            reservationEvent.updateAction
                          )}
                        </span>
                      )
                    }
                    column6={
                      <>
                        {reservationEvent.cisId && (
                          <LabelTooltip
                            target={
                              reservationEvent.cisId?.startsWith('cis.user') ? (
                                <CisLink userId={getIdElement(reservationEvent.cisId)} />
                              ) : (
                                reservationEvent.cisId
                              )
                            }
                            label={userIdToContactNameMap?.[reservationEvent.cisId]}
                          />
                        )}
                      </>
                    }
                    column7={
                      <>
                        {reservationEvent.proxyCisId && (
                          <LabelTooltip
                            target={
                              reservationEvent.proxyCisId?.startsWith('cis.user') ? (
                                <CisLink userId={getIdElement(reservationEvent.proxyCisId)} />
                              ) : (
                                reservationEvent.proxyCisId
                              )
                            }
                            label={userIdToContactNameMap?.[reservationEvent.proxyCisId]}
                          />
                        )}
                      </>
                    }
                    column8={
                      <>
                        <DmcCopyToClipboardTooltip
                          value={reservationEvent.reservation.reservationId}
                          body={
                            ranIdToPersonIdMap?.[reservationEvent.reservation.reservationId] ? (
                              <RanLink
                                ranId={reservationEvent.reservation.reservationId}
                                pid={ranIdToPersonIdMap[reservationEvent.reservation.reservationId]}
                                newTab
                              />
                            ) : (
                              reservationEvent.reservation.reservationId
                            )
                          }
                        />
                        {reservationEvent.reservation.authorizationId && (
                          <>
                            &nbsp;&nbsp;&nbsp;
                            <Link
                              id="reservation-history-print"
                              to={`reservations-authorization/${reservationEvent.reservation.authorizationId}`}
                              target="_blank"
                            >
                              <PersonGroup />
                            </Link>
                          </>
                        )}
                      </>
                    }
                    column9={
                      <LabelTooltip
                        target={getEnumAbbreviation(reservationEvent.reservation.reservationType)}
                        label={t(
                          `reservation.reservationType.${reservationEvent.reservation.reservationType}`,
                          reservationEvent.reservation.reservationType
                        )}
                      />
                    }
                    column10={
                      reservationEvent.reservation.templeSystemId ? (
                        <OsidLink osid={reservationEvent.reservation.templeSystemId} />
                      ) : (
                        t('person.reservations.history.noTempleSystemId', `None`)
                      )
                    }
                    column11={
                      <>
                        {reservationEvent.reservation.ownerId && (
                          <LabelTooltip
                            target={<UserLink userId={reservationEvent.reservation.ownerId} newTab />}
                            label={userIdToContactNameMap?.[reservationEvent.reservation.ownerId]}
                          />
                        )}
                      </>
                    }
                  />
                )
              })}
            </DmcResponsiveTable>
          )}
        </>
      )}
    </>
  )
}

// This page is reached through a subnav route whose key is the tab path, not the
// person, so navigating from one person's reservations history to another's would
// otherwise reuse the same component instance: the requested-id Sets, the RAN/user
// lookup maps and the per-person history keyed by personId would all carry over,
// mixing the previous person's rows into the new person's table and growing
// unbounded across navigations. Keying on the person id remounts instead, which
// resets every piece of that state at once and flips the old instance's isMounted
// ref to false so in-flight Athena polls and lookups can't write back stale data.
export default function PersonReservationsHistory() {
  const { currentPerson } = useContext(PersonContext)
  return <PersonReservationsHistoryContent key={currentPerson?.id} />
}
